import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Cell,
} from "recharts";
import useFileAnalyticFetch from "../hooks/useFileAnalyticFetch";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../firebase";
import AnimatedText from "../../../user/file_management/component/AnimatedText";
import { useAuth } from "../../../../context/AuthContext";

// Color palette for different bars (add more if you have many categories)
const CATEGORY_COLORS = [
  "#1976d2",
  "#388e3c",
  "#fbc02d",
  "#f44336",
  "#7b1fa2",
  "#0097a7",
  "#e65100",
  "#6d4c41",
  "#00c853",
  "#c51162",
];

export default function FileAnalyticsDashboard() {
  const { currentUser } = useAuth();
  const theme = useTheme();

  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoryData, setCategoryData] = useState([]);

  const { data: filesData } = useFileAnalyticFetch(currentUser?.uid);

  useEffect(() => {
    async function fetchSubmissionCounts() {
      setLoading(true);
      try {
        const logsRef = collection(db, "admin", "auditLogs", "logs");
        const querySnapshot = await getDocs(logsRef);

        let approved = 0;
        let rejected = 0;

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.action === "approve") {
            approved++;
          } else if (data.action === "reject") {
            rejected++;
          }
        });

        setApprovedCount(approved);
        setRejectedCount(rejected);
        setTotalSubmissions(querySnapshot.size);
      } catch (error) {
        console.error("Error fetching audit logs:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchSubmissionCounts();
  }, []);

  useEffect(() => {
    if (!filesData) {
      setCategoryData([]);
      return;
    }
    // Aggregate file counts per category
    const categoryCounts = {};
    filesData.forEach((file) => {
      const category = file.classificationCategory || "Uncategorized";
      if (!categoryCounts[category]) {
        categoryCounts[category] = 0;
      }
      categoryCounts[category] += 1;
    });

    const data = Object.entries(categoryCounts).map(([category, count]) => ({
      category,
      count,
    }));

    setCategoryData(data);
  }, [filesData]);

  // Data for the first chart
  const submissionsData = [
    {
      name: "Approved",
      value: approvedCount,
    },
    {
      name: "Rejected",
      value: rejectedCount,
    },
  ];

  // Helper for truncating long category names for Y Axis
  const truncate = (str, n = 18) =>
    str.length > n ? str.slice(0, n - 1) + "…" : str;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.palette.background.default,
        px: { xs: 1, md: 4 },
        py: { xs: 2, md: 6 },
      }}
    >
      <AnimatedText text="File Analytics Dashboard" />
      {loading ? (
        <Typography variant="h6" align="center" sx={{ mt: 4 }}>
          Loading...
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            justifyContent: "center",
            alignItems: "stretch",
            width: "100%",
          }}
        >
          {/* Submissions Card (Original: vertical bar chart) */}
          <Card
            elevation={0}
            sx={{
              background: theme.palette.background.paper,
              borderRadius: 3,
              minHeight: 400,
              p: 3,
              flex: 1.1,
              maxWidth: 500,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.text.primary,
                  mb: 2,
                  fontWeight: 600,
                  letterSpacing: 0.1,
                  background: "#171A1C",
                  textAlign: "center",
                  borderRadius: 2,
                  padding: 1,
                }}
              >
                Submissions
              </Typography>
              <Box sx={{ width: "100%", height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={submissionsData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    style={{ backgroundColor: "#ffffff" }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      interval={0}
                      tick={{ fill: "#000" }}
                      label={{
                        value: "Status",
                        position: "insideBottom",
                        dy: 10,
                      }}
                    />
                    <YAxis
                      allowDecimals={false}
                      label={{
                        value: "Count",
                        angle: -90,
                        position: "insideLeft",
                        offset: 10,
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#23263b",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        fontSize: 13,
                      }}
                      itemStyle={{ color: "#fff" }}
                      labelStyle={{ color: "#fff" }}
                    />
                    <Bar dataKey="value">
                      {submissionsData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            entry.name === "Approved" ? "#00e676" : "#f73378"
                          }
                        />
                      ))}
                      <LabelList dataKey="value" position="insideTop" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  mt: 4,
                  fontWeight: 600,
                  textAlign: "center",
                  fontSize: 20,
                  color: theme.palette.text.primary,
                }}
              >
                Total Number Of Submissions: {totalSubmissions}
              </Typography>
            </CardContent>
          </Card>

          {/* Files Per Category Card (Horizontal bar, ellipsis, colored bars) */}
          <Card
            elevation={0}
            sx={{
              background: theme.palette.background.paper,
              borderRadius: 3,
              minHeight: 400,
              p: 3,
              flex: 1.8,
              maxWidth: 900,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.text.primary,
                  mb: 2,
                  fontWeight: 600,
                  letterSpacing: 0.1,
                  background: "#171A1C",
                  textAlign: "center",
                  borderRadius: 2,
                  padding: 1,
                }}
              >
                Files Per Category
              </Typography>
              <Box sx={{ width: "100%", height: 420 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={categoryData}
                    layout="vertical"
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    style={{ backgroundColor: "#ffffff" }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="count"
                      allowDecimals={false}
                      label={{
                        value: "File Count",
                        position: "insideBottom",
                        dy: 10,
                      }}
                    />
                    <YAxis
                      dataKey="category"
                      type="category"
                      width={140}
                      tick={{
                        fill: "#000",
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                      tickFormatter={(value) => truncate(value, 18)}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#23263b",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        fontSize: 13,
                      }}
                      itemStyle={{ color: "#fff" }}
                      labelStyle={{ color: "#fff" }}
                      formatter={(value, name, props) => [value, "File Count"]}
                      labelFormatter={(label) =>
                        categoryData.find((d) => d.category === label)
                          ?.category || label
                      }
                    />
                    <Bar dataKey="count">
                      {categoryData.map((entry, index) => (
                        <Cell
                          key={`cell-cat-${index}`}
                          fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                        />
                      ))}
                      <LabelList dataKey="count" position="right" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              <Typography
                variant="subtitle1"
                sx={{
                  mt: 4,
                  fontWeight: 600,
                  textAlign: "center",
                  fontSize: 20,
                  color: theme.palette.text.primary,
                }}
              >
                Total Number Of Files:{" "}
                {categoryData.reduce((acc, item) => acc + item.count, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}
